<!-- eslint-disable vue/no-multiple-template-root -->
<template>
  <!-- Live region so screen readers announce page changes on navigation -->
  <div aria-live="polite" aria-atomic="true" class="sr-only">
    {{ pageAnnouncement }}
  </div>
  <main
    :aria-label="`${settingStore.venue_name} application`"
    class="text-fg-g-primary pb-28 lg:pb-0"
    :style="!isCheckoutDesktopFlow ? {} : { 'min-height': `calc(100vh - ${height}px)` }"
    :class="[isCheckoutDesktopFlow ? 'flex justify-between items-start' : '']"
  >
    <GuestHeader
      v-if="(header || isOrderConfirmationPage) && !isCheckoutDesktopFlow"
      :isLoggedIn="isLoggedIn"
    />
    <router-view :class="isCheckoutDesktopFlow ? 'py-16 px-12 w-full shrink-[20]' : ''" />
    <CheckoutDesktopDetailSidebar v-if="isCheckoutDesktopFlow && !isOnboardingFlow" />
  </main>
  <section role="region" aria-label="Footer">
    <FooterComponent v-if="checkoutStore.performance === null" />
    <DevelopmentIndicator />
  </section>
</template>

<script lang="ts">
import { useUserStore } from './stores/user'
import { breakpointsTailwind, useBreakpoints, useResizeObserver } from '@vueuse/core'
import { mapState, mapStores } from 'pinia'
import { computed, defineComponent, nextTick, watch } from 'vue'
import 'vue-advanced-cropper/dist/style.css'
import { useRoute } from 'vue-router'

import DevelopmentIndicator from '@/common/components/atoms/DevelopmentIndicator.vue'
import { useFeatureFlags } from '@/common/composables/useFeatureFlags'
import useReservationCountdown from '@/common/composables/useReservationCountdown'
import FooterComponent from '@/modules/guestUi/components/molecules/FooterComponent.vue'
import GuestHeader from '@/modules/guestUi/components/molecules/GuestHeader.vue'
import CheckoutDesktopDetailSidebar from '@/modules/guestUi/components/organisms/CheckoutDesktopDetailSidebar.vue'
import { usePerformancePaginate } from '@/modules/guestUi/stores/PerformancePaginateStore'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { todayIsoStr } from '@/utils/utils'

export default defineComponent({
  name: 'AppGuest',
  components: {
    CheckoutDesktopDetailSidebar,
    FooterComponent,
    GuestHeader,
    DevelopmentIndicator,
  },
  data() {
    return {
      height: 200,
    }
  },
  async mounted() {
    useResizeObserver(this.footer, this.onResize)
    if (this.isWaitlistEnabled) {
      await this.checkoutStore.getCSDates(todayIsoStr())
    }
    if (this.$route.meta?.onboarding) {
      document.body.setAttribute('class', 'venue-ui')
    } else {
      document.body.setAttribute('class', 'guest-ui bg-body-primary')
    }
  },
  setup() {
    useReservationCountdown()
    const route = useRoute()

    const pageAnnouncement = computed(() => {
      const name = route.name
      const meta = route.meta
      if (typeof meta?.pageTitle === 'string') return meta.pageTitle
      if (typeof name === 'string') return `Page: ${name}`
      return 'Page loaded'
    })

    function announcePageChange() {
      nextTick(() => {
        const h1 = document.querySelector<HTMLHeadingElement>('main h1')
        if (!h1) return
        h1.setAttribute('tabindex', '-1')
        h1.focus()
      })
    }

    watch(
      () => route.path,
      () => {
        announcePageChange()
      },
    )

    return { pageAnnouncement }
  },
  computed: {
    ...mapStores(useSettingStore, useCheckoutStore, usePerformancePaginate),
    ...mapState(useUserStore, ['isLoggedIn']),
    header(): boolean {
      return !!this.$route.meta?.header
    },
    isOrderConfirmationPage(): boolean {
      return this.$route.name === 'order-confirmation'
    },
    footer() {
      return document.querySelector('footer')
    },
    isLargeScreen(): boolean {
      return useBreakpoints(breakpointsTailwind).lg.value
    },
    isCheckoutFlow(): boolean {
      return (
        this.checkoutStore.performance !== null &&
        this.$route.name !== 'setShow' &&
        this.$route.name !== 'showDetails' &&
        this.$route.name !== 'calendar' &&
        this.$route.name !== 'setShowViaCalendar' &&
        !this.isOrderConfirmationPage &&
        this.$route.name !== 'residency-hub'
      )
    },
    isCheckoutDesktopFlow(): boolean {
      return this.isLargeScreen && this.isCheckoutFlow
    },
    isOnboardingFlow(): boolean {
      return !!this.$route.meta?.onboarding
    },
    isWaitlistEnabled(): boolean {
      return useFeatureFlags().isWaitlistSoldOutPerformanceEnabled.value
    },
  },
  async serverPrefetch() {
    await this.performancePaginateStore.initializePagination()
  },
  watch: {
    isCheckoutDesktopFlow: {
      immediate: true,
      handler(value: boolean) {
        this.checkoutStore.setIsDesktopFlow(value)
      },
    },
  },
  methods: {
    onResize() {
      this.height = this.footer?.clientHeight || 200
    },
  },
})
</script>
